import { useMemo } from 'react';

import { CartesianGrid, LineChart, XAxis, YAxis } from 'recharts';
import type { TooltipProps } from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { Button } from '@/components/ui/button';
import { ChartContainer, ChartTooltip, type ChartConfig } from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate } from '@/util/dateUtils';

import { COMPARISON_CHART_COLOR, renderTimeseriesLines } from './renderTimeseriesLines';
import type { CsatBreakdown, TimeseriesDataPoint } from '../../hooks/useOverviewTimeseries';
import { clampSeriesToDomain, type MetricChartConfig } from '../../utils/chartConfig';
import {
  padSeriesToPeriod,
  type PaddedSeriesPoint,
  type PeriodRange,
} from '../../utils/padSeriesToPeriod';
import { AnalyticsEmptyState } from '../aiAgents/AnalyticsEmptyState';
import {
  MetricTooltipCard,
  MetricTooltipRow,
  MetricTooltipSectionLabel,
} from '../tooltip/MetricTooltipCard';

const formatCsatRating = (avg: number | null | undefined, count: number | null | undefined) => {
  if (avg == null) {
    return '–';
  }
  const formatted = avg.toFixed(1);
  return count != null ? `${formatted} (${count})` : formatted;
};

const ConversationSplitRows = ({
  total,
  containmentRate,
  t,
}: {
  total: number;
  containmentRate: number;
  t: (key: string) => string;
}) => {
  const aiCount = Math.round(total * containmentRate);
  const humanCount = total - aiCount;
  return (
    <>
      <MetricTooltipRow
        label={t('analytics.overview_new.ai_agents')}
        value={aiCount.toLocaleString()}
      />
      <MetricTooltipRow
        label={t('analytics.overview_new.human_agents')}
        value={humanCount.toLocaleString()}
      />
    </>
  );
};

const CsatBreakdownRows = ({
  breakdown,
  t,
}: {
  breakdown: CsatBreakdown;
  t: (key: string) => string;
}) => (
  <>
    <MetricTooltipRow
      label={t('analytics.overview_new.user_reviews')}
      value={formatCsatRating(breakdown.avgUserRating, breakdown.userRatingCount)}
    />
    <MetricTooltipRow
      label={t('analytics.overview_new.ai_reviews')}
      value={formatCsatRating(breakdown.avgAiInsights, breakdown.aiInsightsCount)}
    />
  </>
);

export const MetricTooltip = ({
  active,
  payload,
  label,
  config,
  t,
  showComparison,
  csatBreakdown,
  comparisonCsatBreakdown,
}: TooltipProps<ValueType, NameType> & {
  config: MetricChartConfig;
  t: (key: string) => string;
  showComparison?: boolean;
  csatBreakdown?: CsatBreakdown | null;
  comparisonCsatBreakdown?: CsatBreakdown | null;
}) => {
  if (!active || !payload?.length) {
    return null;
  }

  const formattedDate = formatDate(label as string, 'MMM d, yyyy');
  const currentValue = payload.find((p) => p.dataKey === 'value')?.value as number | undefined;
  const comparisonValue = payload.find((p) => p.dataKey === 'comparisonValue')?.value as
    number | undefined;
  const dataPoint = payload.find((p) => p.dataKey === 'value')?.payload;
  const hasComparison = showComparison && comparisonValue != null;

  const previousPeriodLabel = t('analytics.overview_new.previous_period');

  // ── CSAT: user reviews / AI reviews breakdown ────────────────────────
  // The hovered point's own breakdown wins over the period-level props, so the
  // values track the hovered date. The props stay as the fallback for callers
  // that pass a period-level breakdown (the AI Agents card) and for padded gap
  // days, which carry no row of their own.
  const pointCsatBreakdown: CsatBreakdown | null | undefined =
    dataPoint?.csatBreakdown ?? csatBreakdown;
  const pointComparisonCsatBreakdown: CsatBreakdown | null | undefined =
    dataPoint?.comparisonCsatBreakdown ?? comparisonCsatBreakdown;

  if (pointCsatBreakdown) {
    return (
      <MetricTooltipCard title={formattedDate}>
        {/* The dot plots the pooled score, which neither component row equals —
            lead with it so the hover explains the value it points at. */}
        <MetricTooltipRow
          label={t(config.label)}
          value={currentValue != null ? config.valueFormatter(currentValue) : '–'}
        />
        <CsatBreakdownRows breakdown={pointCsatBreakdown} t={t} />
        {hasComparison && pointComparisonCsatBreakdown && (
          <>
            <MetricTooltipSectionLabel>{previousPeriodLabel}</MetricTooltipSectionLabel>
            <MetricTooltipRow
              label={t(config.label)}
              value={config.valueFormatter(comparisonValue)}
            />
            <CsatBreakdownRows breakdown={pointComparisonCsatBreakdown} t={t} />
          </>
        )}
      </MetricTooltipCard>
    );
  }

  // ── Total conversations: AI / Human agent split ──────────────────────
  if (dataPoint?.containmentRate != null && currentValue != null) {
    return (
      <MetricTooltipCard title={formattedDate}>
        <ConversationSplitRows
          total={currentValue}
          containmentRate={dataPoint.containmentRate}
          t={t}
        />
        {hasComparison && dataPoint.comparisonContainmentRate != null && (
          <>
            <MetricTooltipSectionLabel>{previousPeriodLabel}</MetricTooltipSectionLabel>
            <ConversationSplitRows
              total={comparisonValue}
              containmentRate={dataPoint.comparisonContainmentRate}
              t={t}
            />
          </>
        )}
      </MetricTooltipCard>
    );
  }

  // ── Containment (and any other single-value metric) ──────────────────
  return (
    <MetricTooltipCard title={formattedDate}>
      <MetricTooltipRow
        label={t(config.label)}
        value={currentValue != null ? config.valueFormatter(currentValue) : '–'}
      />
      {dataPoint?.reviewCount != null && (
        <MetricTooltipRow
          label={t('analytics.human_agents_overview.tooltip_total_reviews')}
          value={dataPoint.reviewCount.toLocaleString()}
        />
      )}
      {hasComparison && (
        <>
          <MetricTooltipSectionLabel>{previousPeriodLabel}</MetricTooltipSectionLabel>
          <MetricTooltipRow
            label={t(config.label)}
            value={config.valueFormatter(comparisonValue)}
          />
        </>
      )}
    </MetricTooltipCard>
  );
};

export const ChartError = ({ onRetry, t }: { onRetry: () => void; t: (key: string) => string }) => (
  <div className="flex flex-col items-center justify-center gap-2 py-6 text-center">
    <p className="text-sm text-foreground-muted">{t('common.something_wrong')}</p>
    <Button variant="outline-neutral" size="sm" onClick={onRetry}>
      {t('common.reload')}
    </Button>
  </div>
);

/** A timeseries point padded to the full period: `value` may be null (gap days). */
type PaddedTimeseriesPoint = PaddedSeriesPoint<TimeseriesDataPoint>;

/**
 * Aligns primary and comparison timeseries by index so both can render on the
 * same chart. Both arrays are padded to their own (equal-length) periods first,
 * so index N is the same day-offset in each — the alignment is now correct even
 * when the two periods dropped different empty days.
 */
const mergeTimeseriesData = (
  primary: PaddedTimeseriesPoint[],
  comparison: PaddedTimeseriesPoint[]
): Array<
  PaddedTimeseriesPoint & {
    comparisonValue?: number | null;
    comparisonContainmentRate?: number | null;
    comparisonCsatBreakdown?: CsatBreakdown;
  }
> => {
  return primary.map((point, index) => ({
    ...point,
    comparisonValue: comparison[index]?.value,
    comparisonContainmentRate: comparison[index]?.containmentRate,
    comparisonCsatBreakdown: comparison[index]?.csatBreakdown,
  }));
};

export const TimeseriesChart = ({
  timeseries,
  chartConfig,
  currentConfig,
  t,
  period,
  comparisonPeriod,
  comparisonTimeseries,
  isComparing,
  csatBreakdown,
  comparisonCsatBreakdown,
}: {
  timeseries: {
    isLoading: boolean;
    error?: unknown;
    data: TimeseriesDataPoint[];
    onRefetch: () => void;
  };
  chartConfig: ChartConfig;
  currentConfig: MetricChartConfig;
  t: (key: string) => string;
  /** The selected date range; drives the x-axis so every day renders, data or not. */
  period: PeriodRange;
  /** The previous-period range. Equal length to `period` by construction; required so the
      comparison series can never render without merged comparison values. */
  comparisonPeriod: PeriodRange;
  comparisonTimeseries?: { isLoading: boolean; error?: unknown; data: TimeseriesDataPoint[] };
  isComparing?: boolean;
  csatBreakdown?: CsatBreakdown | null;
  comparisonCsatBreakdown?: CsatBreakdown | null;
}) => {
  const showComparison =
    isComparing &&
    comparisonTimeseries &&
    !comparisonTimeseries.isLoading &&
    !comparisonTimeseries.error &&
    comparisonTimeseries.data.length > 0;

  const chartData = useMemo(() => {
    // Pad to the full selected period so the x-axis spans every day (not just
    // days that returned data), then align the comparison by index — both are
    // now equal-length, full-calendar arrays. Clamp last, null-safely.
    const paddedPrimary = padSeriesToPeriod(timeseries.data, period, currentConfig.fill);
    const merged = showComparison
      ? mergeTimeseriesData(
          paddedPrimary,
          padSeriesToPeriod(comparisonTimeseries?.data ?? [], comparisonPeriod, currentConfig.fill)
        )
      : paddedPrimary;
    return clampSeriesToDomain(merged, currentConfig.domain, 'value');
  }, [
    showComparison,
    timeseries.data,
    comparisonTimeseries,
    period,
    comparisonPeriod,
    currentConfig.domain,
    currentConfig.fill,
  ]);

  if (timeseries.isLoading) {
    return <Skeleton className="aspect-4/1 w-full rounded-lg" />;
  }

  if (timeseries.error) {
    return <ChartError onRetry={timeseries.onRefetch} t={t} />;
  }

  if (timeseries.data.length === 0) {
    return (
      <div className="flex aspect-4/1 w-full items-center justify-center">
        <AnalyticsEmptyState
          title={t('analytics.no_data_available')}
          description={t('analytics.no_data_for_period')}
        />
      </div>
    );
  }

  const mergedConfig: ChartConfig = showComparison
    ? {
        ...chartConfig,
        comparisonValue: {
          label: t('analytics.overview_new.previous_period'),
          color: COMPARISON_CHART_COLOR,
        },
      }
    : chartConfig;

  return (
    <ChartContainer config={mergedConfig} className="aspect-4/1 w-full">
      <LineChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={(value) => formatDate(value, 'MMM d')}
          interval="preserveStartEnd"
          minTickGap={40}
        />
        <YAxis
          domain={currentConfig.domain}
          ticks={currentConfig.ticks}
          tickCount={5}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={currentConfig.tickFormatter ?? currentConfig.valueFormatter}
          width={60}
        />
        <ChartTooltip
          content={
            <MetricTooltip
              config={currentConfig}
              t={t}
              showComparison={!!showComparison}
              csatBreakdown={csatBreakdown}
              comparisonCsatBreakdown={comparisonCsatBreakdown}
            />
          }
        />
        {renderTimeseriesLines({ fill: currentConfig.fill, showComparison: !!showComparison })}
      </LineChart>
    </ChartContainer>
  );
};
